import React, { useState } from 'react';
import { teamColors } from './teamColors';
import Badges from './Badges';
import './Participants.css';

/* Who is taking part: photo (or initial) + name, and "Joined" or "Project sent ✓".
   Shown before and during a challenge, never with the project links. */
const Face = ({ p }) => {
  const [broken, setBroken] = useState(false);
  return (
    <span className="rmpt-face">
      {p.photo && !broken ? <img src={p.photo} alt="" loading="lazy" onError={() => setBroken(true)} /> : <span>{String(p.name || '?').charAt(0)}</span>}
    </span>
  );
};

const Card = ({ p, showTeacher }) => (
  <li className={p.mine ? 'is-mine' : ''}>
    <Face p={p} />
    <b>{p.name}</b>
    {showTeacher && p.teacher && <span className="rmbdg-team">with {p.teacher}</span>}
    <Badges b={p.badges} />
    <small className={p.sent ? 'is-sent' : 'is-joined'}>{p.mine ? (p.sent ? 'You · project sent ✓' : 'You · joined') : p.sent ? 'Project sent ✓' : 'Joined'}</small>
  </li>
);

/* One block per team: "Team Rasha · 5 joined · 2 projects sent", then the students.
   teams (optional) = the server's order + the best-team crown. My team first. */
const TeamGroups = ({ list, teams = [] }) => {
  const groups = new Map();
  list.forEach((p) => {
    const k = p.teacher || '';
    if (!groups.has(k)) groups.set(k, []);
    groups.get(k).push(p);
  });
  const order = teams.map((t) => t.name);
  const keys = [...groups.keys()].sort((a, b) => {
    if (!a) return 1; // no team: last
    if (!b) return -1;
    const mine = (k) => groups.get(k).some((p) => p.mine) || teams.some((t) => t.name === k && t.mine);
    if (mine(a) !== mine(b)) return mine(a) ? -1 : 1;
    const ia = order.indexOf(a);
    const ib = order.indexOf(b);
    if (ia !== -1 || ib !== -1) return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib);
    return groups.get(b).length - groups.get(a).length;
  });
  const colors = teamColors([...teams.map((t) => t.name), ...keys]);
  return (
    <div className="rmpt-teams">
      {keys.map((k) => {
        const people = groups.get(k);
        const sent = people.filter((p) => p.sent).length;
        const t = teams.find((x) => x.name === k);
        const mine = people.some((p) => p.mine) || t?.mine;
        return (
          <section
            key={k || 'none'}
            className={`rmpt-team ${t?.best ? 'is-best' : ''} ${mine ? 'is-mine' : ''}`}
            style={k ? { '--rmpt-team-c': colors.get(k) } : undefined}
          >
            <h5>
              {t?.best && <span aria-hidden="true">👑 </span>}
              {k ? `Team ${k}` : 'Other students'}
              <span>
                {people.length} joined{sent > 0 ? ` · ${sent} ${sent === 1 ? 'project' : 'projects'} sent` : ''}
              </span>
              {t?.best && <small>Best team</small>}
              {mine && !t?.best && <small className="is-me">{list.some((p) => p.mine) ? 'Your team' : 'Your students'}</small>}
            </h5>
            <ul className="rmpt-grid">
              {people.map((p) => (
                <Card key={p.id} p={p} />
              ))}
            </ul>
          </section>
        );
      })}
    </div>
  );
};

const Participants = ({ list = [], max, compact = false, teams }) => {
  if (!list.length) return null;
  if (!compact && !max && list.some((p) => p.teacher)) return <TeamGroups list={list} teams={teams} />;
  const shown = max ? list.slice(0, max) : list;
  const more = list.length - shown.length;

  if (compact) {
    return (
      <div className="rmpt-stack" aria-label={`${list.length} students already joined`}>
        {shown.map((p) => (
          <Face key={p.id} p={p} />
        ))}
        {more > 0 && <span className="rmpt-face rmpt-more">+{more}</span>}
      </div>
    );
  }

  return (
    <ul className="rmpt-grid" aria-label="Students taking part">
      {shown.map((p) => (
        <Card key={p.id} p={p} showTeacher />
      ))}
      {more > 0 && (
        <li className="rmpt-more-card">
          <b>+{more}</b>
          <small>more</small>
        </li>
      )}
    </ul>
  );
};

export default Participants;
